import { FunctionComponent, memo, useEffect, useState } from 'react'
import PathAmountSelect from 'components/Smava/PathAmountSelect'

import { Typography, Link } from '@mui/material'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { useClientDetails } from 'stores/utils/hooks/useClientDetails'
import { useStores } from 'stores/utils/hooks/useStores'
import {
  CreditChangeOption,
  RDI_TYPE
} from 'interfaces/ApolloResponse.interface'
import { useData } from 'stores/utils/hooks/useData'
import { roundUpToNextThousand } from 'utils/loanSelector'
import RCEEditFieldDebtor from 'components/RceEdit/RCEEditFieldDebtor'
import PathCheckboxYesNo from 'components/PathCheckboxYesNo'
import PathAmountInput from 'components/PathAmountInput'
import { Unit } from 'components/Input/Text'
import useDataProcessingConsentTracking from 'hooks/useDataProcessingConsentTracking'
import { usePremiumAndAgeGroupTracking } from 'hooks/usePremiumAndAgeGroupTracking'

interface LabelProps {
  declarationOfConsentAdvertising?: string
}

export const PageText = {
  editPage: 'Weiter',
  editPage_additional: 'Direkt zu den Angeboten'
}

const Label: FunctionComponent<LabelProps & { tenant: string }> = memo(
  ({ declarationOfConsentAdvertising = '', tenant = 'FINANZCHECK' }) => (
    <Typography variant="body2">
      Ich möchte über Angebote von {tenant} informiert werden. Ich kann meine{' '}
      <Link
        href={declarationOfConsentAdvertising}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        onClick={() =>
          trackInAmplitude(AmplitudeEvent.OnNewsletterConsentLinkClicked)
        }
      >
        Einwilligung
      </Link>{' '}
      zukünftig jederzeit widerrufen.
    </Typography>
  )
)

Label.displayName = 'Label'

const RCEEdit = ({
  showTypeAsSelect,
  tenant,
  outerLabel,
  smallTextField
}: {
  showTypeAsSelect: boolean
  tenant: string
  outerLabel: boolean
  smallTextField: boolean
}) => {
  const [isPatched, setIsPatched] = useState(false)
  const {
    traversal: {
      handlePathFormFieldChange,
      removePathsFromPayload,
      overrideDataInTraversal
    },
    traversalSession: { setRceEditInitialData }
  } = useStores()

  usePremiumAndAgeGroupTracking()

  const {
    data: {
      common: { amount },
      debtors: {
        primary: {
          income: { primary }
        }
      }
    }
  } = useData()

  const clientDetails = useClientDetails()

  useDataProcessingConsentTracking()

  useEffect(() => {
    setRceEditInitialData({
      amount: amount,
      income: primary
    })
  }, [])

  useEffect(() => {
    handlePathFormFieldChange(
      'common.amountAdaption',
      CreditChangeOption.nothing,
      false
    )

    const { rounded } = roundUpToNextThousand(amount)

    if (rounded !== amount) {
      handlePathFormFieldChange('common.amount', rounded, false)
    }
    handlePathFormFieldChange('debtors.primary.activeLoans', [], false)
    handlePathFormFieldChange('debtors.primary.numberOfActiveLoans', 0, false)
    handlePathFormFieldChange('debtors.secondary.activeLoans', [], false)
    handlePathFormFieldChange('debtors.secondary.numberOfActiveLoans', 0, false)
    const data = removePathsFromPayload(['common.manuallyAdaptedAmount'])
    overrideDataInTraversal(data)
    handlePathFormFieldChange('common.rdi.type', RDI_TYPE.none, true)
    handlePathFormFieldChange('common.rdi.hasAcceptedConsultation', true, true)
    setIsPatched(true)
  }, [])

  // this is added so that the validations only occur after TPL data has been reset
  if (!isPatched) {
    return
  }

  return (
    <>
      {showTypeAsSelect ? (
        <PathAmountSelect path={'common.amount'} outerLabel smallTextField />
      ) : (
        <PathAmountInput path={'common.amount'} unit={Unit.EURO} />
      )}
      <RCEEditFieldDebtor
        outerLabel={outerLabel}
        smallTextField={smallTextField}
      />
      <PathCheckboxYesNo
        alignOnTop
        path="common.dataProcessingConsent"
        label={
          <Label
            tenant={tenant}
            declarationOfConsentAdvertising={
              clientDetails?.urls?.declarationOfConsentAdvertising
            }
          />
        }
      />
    </>
  )
}

export default memo(RCEEdit)
